import { supabase } from '../lib/supabase';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { logger } from '../utils/logger';

interface ServiceabilityRequest {
  vendorId: string;
  vendorLatitude: number;
  vendorLongitude: number;
  userLatitude: number;
  userLongitude: number;
  vendorAddress?: {
    street_address: string[];
    city: string;
    state: string;
    zip_code: string;
    country: string;
  };
  userAddress?: {
    street_address: string[];
    city: string;
    state: string;
    zip_code: string;
    country: string;
  };
}

interface ServiceabilityResult {
  isServiceable: boolean;
  checkedAt: number;
  fallbackUsed: boolean;
  error?: string;
}

interface CachedServiceability {
  [key: string]: ServiceabilityResult;
}

class DeliveryServiceabilityService {
  private cache: CachedServiceability = {};
  private readonly CACHE_DURATION = 1000 * 60 * 30; // 30 minutes
  private readonly CACHE_KEY = 'uber_serviceability_cache';

  constructor() {
    this.loadCache();
  }

  /**
   * Load serviceability cache from AsyncStorage
   */
  private async loadCache() {
    try {
      const cached = await AsyncStorage.getItem(this.CACHE_KEY);
      if (cached) {
        this.cache = JSON.parse(cached);
        // Clean expired entries
        this.cleanExpiredCache();
      }
    } catch (error) {
      logger.warn('Serviceability', 'Failed to load cache', error);
    }
  }

  /**
   * Save serviceability cache to AsyncStorage
   */
  private async saveCache() {
    try {
      await AsyncStorage.setItem(this.CACHE_KEY, JSON.stringify(this.cache));
    } catch (error) {
      logger.warn('Serviceability', 'Failed to save cache', error);
    }
  }

  /**
   * Clean expired cache entries
   */
  private cleanExpiredCache() {
    const now = Date.now();
    for (const key in this.cache) {
      if (now - this.cache[key].checkedAt > this.CACHE_DURATION) {
        delete this.cache[key];
      }
    }
  }

  /**
   * Generate cache key for vendor-user combination
   */
  private getCacheKey(vendorId: string, userLat: number, userLng: number): string {
    // Round coordinates to reduce cache keys for nearby locations
    const roundedUserLat = Math.round(userLat * 1000) / 1000;
    const roundedUserLng = Math.round(userLng * 1000) / 1000;
    return `${vendorId}_${roundedUserLat}_${roundedUserLng}`;
  }

  /**
   * Check if delivery is serviceable using Uber Direct
   */
  async checkServiceability(request: ServiceabilityRequest): Promise<ServiceabilityResult> {
    const cacheKey = this.getCacheKey(request.vendorId, request.userLatitude, request.userLongitude);
    
    // Check cache first
    const cached = this.cache[cacheKey];
    if (cached && (Date.now() - cached.checkedAt) < this.CACHE_DURATION) {
      logger.debug('Serviceability', `Cache hit for ${cacheKey}`);
      return cached;
    }

    // Try Uber Direct serviceability check
    try {
      const result = await this.checkUberDirectServiceability(request);
      
      // Cache the result
      this.cache[cacheKey] = result;
      this.saveCache();
      
      return result;
    } catch (error: any) {
      const errorMessage = error?.message || String(error);
      logger.error('Serviceability', 'Uber Direct check failed', errorMessage);
      
      // For rate limiting, use fallback but don't cache as long (5 minutes)
      const isRateLimited = errorMessage.includes('rate') || errorMessage.includes('RATE_LIMITED') || errorMessage.includes('429');
      
      // Fallback to distance-based check
      const fallbackResult = this.fallbackDistanceCheck(request);
      
      // Cache fallback result with shorter duration
      // Rate limited: 5 minutes, other errors: 15 minutes
      const cacheExpiryOffset = isRateLimited 
        ? this.CACHE_DURATION * 0.17 // ~5 minutes
        : this.CACHE_DURATION * 0.5;  // 15 minutes
      
      this.cache[cacheKey] = {
        ...fallbackResult,
        checkedAt: Date.now() - (this.CACHE_DURATION - cacheExpiryOffset),
        error: isRateLimited ? 'Rate limited, using fallback' : errorMessage
      };
      this.saveCache();
      
      return {
        ...fallbackResult,
        error: isRateLimited ? 'Rate limited, using fallback' : errorMessage
      };
    }
  }

  /**
   * Check serviceability using Uber Direct quote endpoint
   */
  private async checkUberDirectServiceability(request: ServiceabilityRequest): Promise<ServiceabilityResult> {
    logger.debug('Serviceability', `Checking Uber Direct serviceability for vendor ${request.vendorId}`);

    // Create minimal delivery addresses for quote request
    const pickupAddress = request.vendorAddress || {
      street_address: ["Business Address"],
      city: "New York",
      state: "NY", 
      zip_code: "10001",
      country: "US"
    };

    const dropoffAddress = request.userAddress || {
      street_address: ["Customer Address"],
      city: "New York", 
      state: "NY",
      zip_code: "10001",
      country: "US"
    };

    try {
      // Use the uber-quote edge function
      // Production mode - no test_specifications needed
      const { data, error } = await supabase.functions.invoke('uber-quote', {
        body: {
          pickup_address: pickupAddress,
          dropoff_address: dropoffAddress,
          pickup_latitude: request.vendorLatitude,
          pickup_longitude: request.vendorLongitude,
          dropoff_latitude: request.userLatitude,
          dropoff_longitude: request.userLongitude,
        }
      });

      // Handle edge function errors
      if (error) {
        logger.error('Serviceability', 'Uber quote edge function error', error);
        // Check if it's a rate limit error - don't throw, use fallback
        if (error.message?.includes('rate') || error.message?.includes('429')) {
          logger.warn('Serviceability', 'Rate limited, will use fallback');
          throw new Error('RATE_LIMITED');
        }
        throw error;
      }

      // Handle non-2xx responses from Uber API (edge function returns data even on error)
      if (data) {
        // Check for rate limit error in response
        if (data.error === 'rate_limited' || data.error === 'too_many_requests' || data.status === 429) {
          logger.warn('Serviceability', 'Rate limited by Uber API, will use fallback');
          throw new Error('RATE_LIMITED');
        }

        // Check for other API errors
        if (data.error && data.error !== 'rate_limited') {
          logger.warn('Serviceability', `Uber API error: ${data.error} - ${data.message || ''}`);
          // For serviceability checks, treat API errors as "not serviceable" rather than throwing
          return {
            isServiceable: false,
            checkedAt: Date.now(),
            fallbackUsed: false,
            error: data.message || data.error
          };
        }

        // Success - got a quote
        if (data.id) {
          logger.debug('Serviceability', `Serviceable - Quote ID: ${data.id}`);
          return {
            isServiceable: true,
            checkedAt: Date.now(),
            fallbackUsed: false
          };
        }
      }

      // No quote returned - not serviceable
      logger.debug('Serviceability', 'Not serviceable - No quote returned');
      return {
        isServiceable: false,
        checkedAt: Date.now(),
        fallbackUsed: false
      };
    } catch (error: any) {
      // Re-throw rate limit errors so they trigger fallback
      if (error?.message === 'RATE_LIMITED' || error?.message?.includes('rate')) {
        throw error;
      }
      
      logger.error('Serviceability', 'Uber Direct API error', error);
      // For other errors, don't throw - let the fallback handle it
      // This prevents serviceability checks from breaking the app
      throw error;
    }
  }

  /**
   * Fallback distance-based serviceability check
   */
  private fallbackDistanceCheck(request: ServiceabilityRequest): ServiceabilityResult {
    logger.debug('Serviceability', `Using fallback distance check for vendor ${request.vendorId}`);
    
    // Calculate distance between vendor and user
    const distance = this.calculateDistance(
      request.vendorLatitude,
      request.vendorLongitude,
      request.userLatitude,
      request.userLongitude
    );

    // Use conservative 8-mile radius for fallback (smaller than default 10 to be safe)
    const maxDistance = 8;
    const isServiceable = distance <= maxDistance;

    logger.debug('Serviceability', `Distance: ${distance.toFixed(2)} miles, Max: ${maxDistance} miles, Serviceable: ${isServiceable}`);

    return {
      isServiceable,
      checkedAt: Date.now(),
      fallbackUsed: true
    };
  }

  /**
   * Calculate distance between two coordinates
   */
  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 3958.8; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  /**
   * Batch check serviceability for multiple vendors
   */
  async batchCheckServiceability(requests: ServiceabilityRequest[]): Promise<ServiceabilityResult[]> {
    const promises = requests.map(request => this.checkServiceability(request));
    return Promise.all(promises);
  }

  /**
   * Clear the serviceability cache
   */
  async clearCache() {
    this.cache = {};
    await AsyncStorage.removeItem(this.CACHE_KEY);
    logger.info('Serviceability', 'Cache cleared');
  }

  /**
   * Clear cache entries for specific vendors (when their addresses change)
   */
  async clearVendorCache(vendorIds: string[]) {
    let clearedCount = 0;
    
    for (const key in this.cache) {
      // Extract vendor ID from cache key format: "vendorId_lat_lng"
      const vendorId = key.split('_')[0];
      if (vendorIds.includes(vendorId)) {
        delete this.cache[key];
        clearedCount++;
      }
    }
    
    if (clearedCount > 0) {
      await this.saveCache();
      logger.info('Serviceability', `Cleared ${clearedCount} cache entries for ${vendorIds.length} vendors with address changes`);
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    const totalEntries = Object.keys(this.cache).length;
    const now = Date.now();
    const expiredEntries = Object.values(this.cache).filter(
      entry => (now - entry.checkedAt) > this.CACHE_DURATION
    ).length;
    
    return {
      totalEntries,
      expiredEntries,
      validEntries: totalEntries - expiredEntries,
      cacheSize: JSON.stringify(this.cache).length
    };
  }
}

export const deliveryServiceabilityService = new DeliveryServiceabilityService();
export type { ServiceabilityRequest, ServiceabilityResult };